import React, { Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './contexts/LanguageContext';
import ErrorBoundary from './components/ErrorBoundary';
import NetworkStatusNotification from './components/NetworkStatus';
import SEO, { PageSEO } from './components/SEO';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import BackToTop from './components/BackToTop';
import ScrollToTop from './components/ScrollToTop';
import { AdvancedLoader } from './utils/lazyLoad.jsx';
import { initializeIntelligentPreload } from './utils/lazyLoad.jsx';
import { usePerformanceMonitor } from './hooks/usePerformanceMonitor';
import { useAnalytics } from './hooks/useAnalytics';

// 懒加载页面组件
const HomePage = React.lazy(() => import('./pages/HomePage'));
const ProductPage = React.lazy(() => import('./pages/ProductPage'));
const DemoPage = React.lazy(() => import('./pages/DemoPage'));
const SolutionsPage = React.lazy(() => import('./pages/SolutionsPage'));
const CasesPage = React.lazy(() => import('./pages/CasesPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const ResourcesPage = React.lazy(() => import('./pages/ResourcesPage'));

// 页面加载组件
const PageLoader = () => (
  <AdvancedLoader
    type="wave"
    message="页面加载中..."
    className="min-h-screen"
  />
);

// 路由配置
const routeConfigs = [
  { path: '/', component: HomePage, seoPage: 'home' },
  { path: '/product', component: ProductPage, seoPage: 'product' },
  { path: '/demo', component: DemoPage, seoPage: 'demo' },
  { path: '/solutions', component: SolutionsPage, seoPage: 'solutions' },
  { path: '/cases', component: CasesPage, seoPage: 'cases' },
  // { path: '/pricing', component: PricingPage, seoPage: 'pricing' }, // 暂时隐藏定价页面
  // { path: '/resources', component: ResourcesPage, seoPage: 'resources' } // 暂时隐藏资源页面
];

function App() {
  const location = useLocation();
  const { trackPageView } = useAnalytics();

  // 性能监控
  usePerformanceMonitor({
    enableWebVitals: true,
    enableResourceTiming: true,
    enableNavigationTiming: true
  });

  // 初始化智能预加载
  useEffect(() => {
    initializeIntelligentPreload();
  }, []);

  // 页面刷新时滚动到顶部
  useEffect(() => {
    // 在页面加载完成后立即滚动到顶部
    window.scrollTo(0, 0);
    
    // 处理页面刷新的情况
    const handleLoad = () => {
      window.scrollTo(0, 0);
    };
    
    // 如果页面已经加载完成，立即滚动
    if (document.readyState === 'complete') {
      window.scrollTo(0, 0);
    } else {
      window.addEventListener('load', handleLoad);
    }
    
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  // 页面浏览追踪
  useEffect(() => {
    const currentRoute = routeConfigs.find(route => route.path === location.pathname);
    const pageName = currentRoute?.seoPage || 'unknown';
    trackPageView(pageName, {
      path: location.pathname,
      search: location.search
    });
  }, [location, trackPageView]);

  return (
    <HelmetProvider>
      <ErrorBoundary showDetails={process.env.NODE_ENV === 'development'}>
        <LanguageProvider>
          <div className="min-h-screen bg-white">
            {/* 路由切换时滚动到顶部 */}
            <ScrollToTop />
            
            {/* 默认SEO */}
            <SEO />

            {/* 网络状态通知 */}
            <NetworkStatusNotification />

            <header>
              <Navbar />
            </header>

            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {routeConfigs.map(({ path, component: Component, seoPage }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <>
                          <PageSEO page={seoPage} />
                          <Component />
                        </>
                      }
                    />
                  ))}
                </Routes>
              </Suspense>
            </ErrorBoundary>

            <Footer />

            {/* 返回顶部按钮 */}
            <BackToTop />
          </div>
        </LanguageProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;